import type { MessageKey } from '@ralph/contracts'
import type { ServerMessages } from '@/i18n/messages/en/errors/index.ts'

/**
 * What the auth domain's refusals say, in English (the source of truth AND the type).
 *
 * Half of these are read by somebody who does not have an account yet, on the sign-in page,
 * and cannot be routed around: a person who cannot get in cannot switch the language to read
 * why they cannot get in. That is the whole reason the locale resolves from the brand and the
 * browser before any session exists (LOC-002, LOC-004).
 *
 * So they say what to DO. "Start again" and "ask an administrator" are the two real outcomes,
 * and eleven different ways for a token to be unusable are eleven operator distinctions, kept
 * in the English `message` where the operator is.
 */
export const auth: Pick<ServerMessages, Extract<MessageKey, `auth.${string}`>> = {
  // --- signing in ----------------------------------------------------------

  'auth.local_sign_in_disabled': () => 'Local sign-in is not enabled on this deployment.',
  'auth.sign_in_required': () => 'Sign in to change your preferences.',
  'auth.sign_in_failed': () => 'Your sign-in could not be completed. Start again.',
  'auth.sign_in_session_expired': () => 'This sign-in took too long, or was already used. Start again.',
  'auth.incorrect_password': () => 'That password is not correct.',
  'auth.provider_required': () => 'Choose a way to sign in.',
  'auth.provider_not_configured': ({ provider }) =>
    `There is no sign-in method called “${provider}” on this deployment.`,
  'auth.provider_response_unusable': () =>
    'Your identity provider did not return everything this platform needs to sign you in. ' +
    'Ask an administrator to check the sign-in configuration.',
  'auth.tenant_not_permitted': ({ tenant }) => `Sign-in from tenant ${tenant} is not allowed here.`,
  'auth.email_domain_not_permitted': ({ domains }) => `Sign-in here is limited to ${domains}.`,
  'auth.account_deactivated': () => 'This account has been deactivated on this platform.',
  'auth.email_claimed_by_other_provider': ({ email, provider }) =>
    `${email} is already registered through the “${provider}” sign-in method.`,
  'auth.account_unavailable': () => 'Your account is no longer available.',

  // --- signing in somewhere else -------------------------------------------
  //
  // A connector's or a connected system's authorization round trip, not this platform's. They
  // read close to `auth.sign_in_session_expired` above and are deliberately not merged with
  // it: the remedy is to start the CONNECTOR's sign-in again, on a screen inside the product,
  // by somebody who is already signed in here.

  'auth.sign_in_not_pending': () => 'This sign-in has already been completed, or was never started here.',
  'auth.sign_in_expired': () => 'This sign-in took too long and has expired. Start it again.',

  // --- your own preferences ------------------------------------------------

  'auth.locale_not_supported': () => 'That is not a language this platform speaks.',
  'auth.time_zone_unknown': ({ timeZone }) => `“${timeZone}” is not a timezone this platform knows.`,

  // --- administering accounts and roles ------------------------------------

  // Said by the access gate, the people directory, the group store and the sharing quota
  // alike. One sentence about an account, in one place.
  'auth.account_not_found': () => 'There is no account with that id.',
  'auth.account_deactivated_for_role': () =>
    'That account is deactivated, so a role would confer nothing. Reactivate it first.',
  'auth.last_administrator': () =>
    'This is the only administrator left, so this change would leave the platform with nobody who can ' +
    'administer it. Give somebody else the administrator role first.',
  'auth.cannot_remove_own_admin': () =>
    'You cannot remove your own administrator access. Ask another administrator to do it.',
  'auth.settings_invalid': () => 'Those settings are not valid.',
  'auth.role_already_granted': () => 'This account already holds that role from a grant here.',
  'auth.role_already_denied': () => 'That role is already denied for this account.',
  'auth.role_already_prepared': () => 'That role is already waiting for this address.',
  'auth.grant_not_found': () => 'That grant no longer exists.',
  'auth.grant_already_in_effect': () => 'That grant is already in effect.',
  'auth.grant_not_claimed': () => 'No account has claimed that grant yet.',
  'auth.grant_needs_another_administrator': () =>
    'A second administrator has to approve this — you are the one who asked for it.',
  'auth.prepared_grant_not_waiting': () => 'That prepared grant is no longer waiting.'
}
